import { AccountApi } from '@/api/account.api';
import { NaluriRefreshTokenError } from '@/errors';
import type { TokenStore } from '@/utils/tokenStore';

export class ApiClient {
  private readonly fetchClient: typeof fetch;
  private readonly tokenStore: TokenStore;
  private readonly accountApi: AccountApi;
  private readonly onRefreshTokenError?: () => Promise<void>;

  constructor(
    tokenStore: TokenStore,
    accountApi: AccountApi,
    onRefreshTokenError?: () => Promise<void>,
  ) {
    this.fetchClient =
      typeof window === 'undefined' ? fetch : window.fetch.bind(window);
    this.tokenStore = tokenStore;
    this.accountApi = accountApi;
    this.onRefreshTokenError = onRefreshTokenError;
  }

  public async fetch(url: string, options: RequestInit): Promise<Response> {
    return await this.doFetch(url, options);
  }

  private async doFetch(
    url: string,
    options: RequestInit,
    retried = false,
  ): Promise<Response> {
    const accessToken = this.tokenStore.getItem('accessToken');

    if (accessToken) {
      options.headers = {
        ...options.headers,
        Authorization: `Bearer ${accessToken}`,
      };
    }

    const response = await this.fetchClient(url, options);

    if (response.status === 401 && retried) {
      if (this.onRefreshTokenError) {
        await this.onRefreshTokenError();

        // @ts-expect-error
        return {
          ok: false,
          json: () => ({
            message: 'Refresh flow retried but failed',
            statusCode: 8888,
          }),
          status: 401,
        } as Response;
      }

      throw new NaluriRefreshTokenError();
    }

    if (response.status === 401) {
      const refreshToken = this.tokenStore.getItem('refreshToken');

      if (!refreshToken) {
        if (this.onRefreshTokenError) {
          await this.onRefreshTokenError();
          // @ts-expect-error
          return {
            ok: false,
            json: () => ({
              message: 'Missing refresh token',
              statusCode: 8888,
            }),
            status: 401,
          } as Response;
        }

        throw new NaluriRefreshTokenError();
      }

      try {
        const { accessToken } = await this.accountApi.refresh(refreshToken);

        this.tokenStore.setItem('accessToken', accessToken);
      } catch (error) {
        if (this.onRefreshTokenError) {
          await this.onRefreshTokenError();
          // @ts-expect-error
          return {
            ok: false,
            json: () => ({
              statusCode: 8888,
              message: `Refresh API failed: ${
                (error as Error).message || error
              }`,
            }),
            status: 401,
          } as Response;
        }
        throw new NaluriRefreshTokenError();
      }

      return this.doFetch(url, options, true);
    }

    return response;
  }
}
